import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowLeft,
  FiBarChart2,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiExternalLink,
  FiEye,
  FiMessageCircle,
  FiPauseCircle,
  FiRefreshCw,
  FiSend,
  FiShield,
  FiTrendingUp,
  FiTrash2,
  FiUsers,
  FiX,
  FiXCircle,
  FiZap,
} from "react-icons/fi";
import {
  adminApproveReview,
  adminRejectReview,
  fetchCommentEvents,
  fetchCommentMetrics,
  fetchCommentMetricsPerUser,
  fetchCommentOperations,
  fetchCommentOverviewStats,
  fetchCommentQueue,
  fetchCommentUserDetail,
  fetchCommentUsers,
  flushStaleQueueItems,
  pauseAllUserCommentConfigs,
  purgeCommentQueue,
} from "./services/adminCommentService";

const TAB_CONFIG = [
  {
    key: "overview",
    label: "Tổng quan",
    icon: FiTrendingUp,
    href: "/admin/lead-engine",
  },
  {
    key: "users",
    label: "Khách hàng",
    icon: FiUsers,
    href: "/admin/lead-engine/customers",
  },
  {
    key: "operations",
    label: "Vận hành",
    icon: FiShield,
    href: "/admin/lead-engine/operations",
  },
  {
    key: "metrics",
    label: "Analytics",
    icon: FiBarChart2,
    href: "/admin/lead-engine/analytics",
  },
];

function resolveActiveTab(pathname = "", fallback = "overview") {
  if (pathname.startsWith("/admin/lead-engine/customers")) {
    return "users";
  }

  if (pathname.startsWith("/admin/lead-engine/operations")) {
    return "operations";
  }

  if (pathname.startsWith("/admin/lead-engine/analytics")) {
    return "metrics";
  }

  if (pathname.startsWith("/admin/lead-engine")) {
    return "overview";
  }

  return fallback;
}

function formatDate(timestamp) {
  if (!timestamp) {
    return "—";
  }

  return new Date(timestamp).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeTime(timestamp) {
  if (!timestamp) {
    return "—";
  }

  const diffMinutes = Math.max(
    1,
    Math.floor((Date.now() - Number(timestamp || 0)) / 60000)
  );

  if (diffMinutes < 60) {
    return `${diffMinutes} phút trước`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} giờ trước`;
  }

  return `${Math.floor(diffHours / 24)} ngày trước`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function notifyReloadRequired(message) {
  window.alert(
    message ||
      "Dữ liệu admin Lead Engine là snapshot. Reload trang để xem thay đổi mới nhất."
  );
}

function StatusBadge({ status, size = "sm" }) {
  const map = {
    queued: {
      bg: "bg-amber-100",
      text: "text-amber-700",
      label: "Đợi xử lý",
      icon: FiClock,
    },
    processing: {
      bg: "bg-blue-100",
      text: "text-blue-700",
      label: "Đang xử lý",
      icon: FiActivity,
    },
    sent: {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      label: "Đã gửi",
      icon: FiCheckCircle,
    },
    ignored: {
      bg: "bg-slate-100",
      text: "text-slate-600",
      label: "Bỏ qua",
      icon: FiXCircle,
    },
    failed: {
      bg: "bg-rose-100",
      text: "text-rose-700",
      label: "Lỗi",
      icon: FiAlertTriangle,
    },
    manual_followup: {
      bg: "bg-violet-100",
      text: "text-violet-700",
      label: "Cần follow-up",
      icon: FiEye,
    },
    needs_review: {
      bg: "bg-violet-100",
      text: "text-violet-700",
      label: "Cần duyệt",
      icon: FiEye,
    },
    pending: {
      bg: "bg-amber-100",
      text: "text-amber-700",
      label: "Chờ duyệt",
      icon: FiClock,
    },
    approved: {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      label: "Đã duyệt",
      icon: FiCheckCircle,
    },
    rejected: {
      bg: "bg-rose-100",
      text: "text-rose-700",
      label: "Từ chối",
      icon: FiXCircle,
    },
  };

  const state = map[status] || {
    bg: "bg-slate-100",
    text: "text-slate-500",
    label: status || "—",
    icon: FiActivity,
  };
  const Icon = state.icon;
  const textSize = size === "xs" ? "text-[10px]" : "text-xs";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${state.bg} ${state.text} ${textSize}`}
    >
      <Icon className="h-3 w-3" />
      {state.label}
    </span>
  );
}

function LeadBadge({ state }) {
  if (!state) {
    return null;
  }

  const color =
    state === "hot"
      ? "bg-rose-100 text-rose-700"
      : state === "warm"
      ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-600";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${color}`}
    >
      {state}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colorMap = {
    blue: "from-blue-500 to-blue-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    violet: "from-violet-500 to-violet-600",
    slate: "from-slate-500 to-slate-600",
    cyan: "from-cyan-500 to-cyan-600",
    teal: "from-teal-500 to-teal-600",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm ${
            colorMap[color] || colorMap.blue
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {typeof value === "number" ? value.toLocaleString("vi-VN") : value}
          </p>
          {sub ? <p className="mt-1 text-[11px] text-slate-400">{sub}</p> : null}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <Icon className="h-6 w-6" />
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {description ? (
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function LoadingState({ label = "Đang tải..." }) {
  return (
    <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-12 text-slate-500 shadow-sm">
      <FiRefreshCw className="mr-2 h-5 w-5 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
      <FiAlertTriangle className="mx-auto mb-2 h-8 w-8 text-rose-500" />
      <p className="text-sm text-rose-700">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
        >
          Thử lại
        </button>
      ) : null}
    </div>
  );
}

function SectionTitle({ title, description, actions }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await fetchCommentOverviewStats();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingState label="Đang tải tổng quan engine..." />;
  }

  if (error && !stats) {
    return <ErrorState message={error} onRetry={load} />;
  }

  if (!stats) {
    return null;
  }

  const errorRate =
    Number(stats.today?.received || 0) > 0
      ? ((Number(stats.today?.failed || 0) / Number(stats.today.received)) * 100)
          .toFixed(1)
      : "0";

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Tổng quan hệ thống"
        description="Theo dõi trạng thái vận hành trong ngày, mức dùng của khách hàng và xu hướng lỗi."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={FiUsers}
          label="Khách hàng đang dùng"
          value={stats.totalUsers}
          color="blue"
        />
        <StatCard
          icon={FiActivity}
          label="Config đang bật"
          value={stats.enabledConfigs}
          sub={`/${formatNumber(stats.totalConfigs)} tổng cấu hình`}
          color="emerald"
        />
        <StatCard
          icon={FiMessageCircle}
          label="Fanpage active"
          value={stats.totalPages}
          color="cyan"
        />
        <StatCard
          icon={FiDatabase}
          label="Queue hiện tại"
          value={stats.queue?.total || 0}
          sub={
            Number(stats.queue?.stale || 0) > 0
              ? `${formatNumber(stats.queue?.stale)} item stale`
              : "Ổn định"
          }
          color={Number(stats.queue?.stale || 0) > 0 ? "rose" : "slate"}
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Hôm nay</h4>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          <StatCard
            icon={FiMessageCircle}
            label="Comment nhận"
            value={stats.today?.received || 0}
            color="blue"
          />
          <StatCard
            icon={FiSend}
            label="Đã gửi"
            value={stats.today?.sent || 0}
            color="emerald"
          />
          <StatCard
            icon={FiXCircle}
            label="Bỏ qua"
            value={stats.today?.ignored || 0}
            color="slate"
          />
          <StatCard
            icon={FiAlertTriangle}
            label="Lỗi"
            value={stats.today?.failed || 0}
            color="rose"
          />
          <StatCard
            icon={FiEye}
            label="Cần follow-up"
            value={
              stats.today?.manual_followup || stats.today?.needs_review || 0
            }
            color="violet"
          />
          <StatCard
            icon={FiZap}
            label="AI Calls"
            value={stats.today?.aiCalls || 0}
            sub={`~${((Number(stats.today?.tokensApprox || 0) || 0) / 1000).toFixed(1)}K tokens`}
            color="amber"
          />
        </div>
      </div>

      {Array.isArray(stats.weekly) && stats.weekly.length > 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h4 className="text-sm font-semibold text-slate-700">
              Xu hướng 7 ngày
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-3 text-left font-medium">Ngày</th>
                  <th className="px-4 py-3 text-right font-medium">Nhận</th>
                  <th className="px-4 py-3 text-right font-medium">Gửi</th>
                  <th className="px-4 py-3 text-right font-medium">Lỗi</th>
                  <th className="px-4 py-3 text-right font-medium">Tỉ lệ lỗi</th>
                  <th className="px-4 py-3 text-right font-medium">AI Calls</th>
                </tr>
              </thead>
              <tbody>
                {stats.weekly.map((day) => {
                  const dayErrorRate =
                    Number(day.received || 0) > 0
                      ? ((Number(day.failed || 0) / Number(day.received)) * 100).toFixed(
                          1
                        )
                      : "0";

                  return (
                    <tr
                      key={day.date}
                      className="border-t border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {day.date}
                      </td>
                      <td className="px-4 py-3 text-right">{formatNumber(day.received)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">
                        {formatNumber(day.sent)}
                      </td>
                      <td className="px-4 py-3 text-right text-rose-600">
                        {formatNumber(day.failed)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={
                            Number(dayErrorRate) > 10
                              ? "font-medium text-rose-600"
                              : "text-slate-500"
                          }
                        >
                          {dayErrorRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600">
                        {formatNumber(day.aiCalls)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {Array.isArray(stats.topPages) && stats.topPages.length > 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h4 className="text-sm font-semibold text-slate-700">
              Top fanpage theo sản lượng gửi hôm nay
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-3 text-left font-medium">#</th>
                  <th className="px-4 py-3 text-left font-medium">Page ID</th>
                  <th className="px-4 py-3 text-right font-medium">Nhận</th>
                  <th className="px-4 py-3 text-right font-medium">Gửi</th>
                  <th className="px-4 py-3 text-right font-medium">Lỗi</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPages.map((page, index) => (
                  <tr
                    key={`${page.pageId}-${index}`}
                    className="border-t border-slate-100 hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3 text-slate-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                        {page.pageId}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatNumber(page.received)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {formatNumber(page.sent)}
                    </td>
                    <td className="px-4 py-3 text-right text-rose-600">
                      {formatNumber(page.failed)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
        <div
          className={`h-3 w-3 rounded-full ${
            Number(stats.queue?.stale || 0) > 0
              ? "animate-pulse bg-rose-500"
              : Number(errorRate) > 15
              ? "animate-pulse bg-amber-500"
              : "bg-emerald-500"
          }`}
        />
        <div>
          <p className="text-sm font-medium text-slate-800">
            {Number(stats.queue?.stale || 0) > 0
              ? "Có items bị kẹt trong queue"
              : Number(errorRate) > 15
              ? "Tỉ lệ lỗi đang cao"
              : "Hệ thống đang ổn định"}
          </p>
          <p className="text-xs text-slate-500">
            Tỉ lệ lỗi hôm nay: {errorRate}% • Queue:{" "}
            {formatNumber(stats.queue?.queued || 0)} đợi,{" "}
            {formatNumber(stats.queue?.processing || 0)} đang xử lý
          </p>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processing, setProcessing] = useState("");
  const [error, setError] = useState("");
  const [expandedReview, setExpandedReview] = useState("");
  const [editingReply, setEditingReply] = useState({});

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await fetchCommentUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadDetail = useCallback(async (userId) => {
    try {
      setDetailLoading(true);
      setError("");
      const data = await fetchCommentUserDetail(userId);
      setUserDetail(data);
      setSelectedUser(userId);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handlePauseAll = useCallback(
    async (userId) => {
      if (
        !window.confirm(
          "Bạn chắc chắn muốn tạm dừng tất cả config Lead Conversion Engine của user này?"
        )
      ) {
        return;
      }

      try {
        setProcessing(userId);
        await pauseAllUserCommentConfigs(userId);
        notifyReloadRequired(
          "Đã pause toàn bộ config. Reload trang để xem snapshot Lead Engine mới nhất."
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setProcessing("");
      }
    },
    []
  );

  const handleApprove = useCallback(
    async (review) => {
      try {
        setProcessing(`approve-${review.reviewId}`);
        await adminApproveReview({
          userId: selectedUser,
          configId: review.configId,
          reviewId: review.reviewId,
          replyText:
            editingReply[review.reviewId] || review.suggestedReply || "",
        });
        notifyReloadRequired(
          "Đã duyệt review. Reload trang để xem snapshot Lead Engine mới nhất."
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setProcessing("");
      }
    },
    [editingReply, selectedUser]
  );

  const handleReject = useCallback(
    async (review) => {
      try {
        setProcessing(`reject-${review.reviewId}`);
        await adminRejectReview({
          userId: selectedUser,
          configId: review.configId,
          reviewId: review.reviewId,
          reason: "Rejected by admin",
        });
        notifyReloadRequired(
          "Đã từ chối review. Reload trang để xem snapshot Lead Engine mới nhất."
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setProcessing("");
      }
    },
    [selectedUser]
  );

  if (loading) {
    return <LoadingState label="Đang tải danh sách khách hàng..." />;
  }

  if (error && !users.length) {
    return <ErrorState message={error} onRetry={loadUsers} />;
  }

  if (selectedUser && userDetail) {
    const pendingReviews = (userDetail.reviews || []).filter(
      (review) => review.status === "pending"
    );

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => {
                setSelectedUser("");
                setUserDetail(null);
              }}
              className="btn-icon"
              title="Quay lại danh sách"
            >
              <FiArrowLeft />
            </button>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {userDetail.displayName || userDetail.userId}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {userDetail.email || "Không có email"} • {userDetail.userId}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handlePauseAll(selectedUser)}
            disabled={processing === selectedUser}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
          >
            <FiPauseCircle />
            Pause tất cả config
          </button>
        </div>

        {error ? <ErrorState message={error} onRetry={() => loadDetail(selectedUser)} /> : null}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatCard
            icon={FiMessageCircle}
            label="Tổng thread"
            value={userDetail.threadStats?.total || 0}
            color="blue"
          />
          <StatCard
            icon={FiSend}
            label="Đã gửi"
            value={userDetail.threadStats?.sent || 0}
            color="emerald"
          />
          <StatCard
            icon={FiAlertTriangle}
            label="Lỗi"
            value={userDetail.threadStats?.failed || 0}
            color="rose"
          />
          <StatCard
            icon={FiEye}
            label="Cần follow-up"
            value={
              userDetail.threadStats?.manual_followup ||
              userDetail.threadStats?.needs_review ||
              0
            }
            color="violet"
          />
          <StatCard
            icon={FiXCircle}
            label="Bỏ qua"
            value={userDetail.threadStats?.ignored || 0}
            color="slate"
          />
        </div>

        <div className="space-y-3">
          <SectionTitle
            title={`Cấu hình (${formatNumber(userDetail.configs?.length || 0)})`}
            description="Kiểm tra trạng thái kích hoạt, số fanpage và Telegram alert của từng config."
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {(userDetail.configs || []).map((config) => {
              const telegramEnabled = Boolean(config.telegramAlert?.enabled);
              const telegramConnected = Boolean(config.telegramAlert?.chatId);

              return (
                <div
                  key={config.id}
                  className={`rounded-2xl border p-4 ${
                    config.enabled
                      ? "border-emerald-200 bg-emerald-50/40"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900">
                        {config.name || "—"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatNumber((config.selectedPages || []).length)} fanpage •
                        {config.pausedByAdmin ? (
                          <span className="ml-1 font-medium text-rose-600">
                            Paused by admin
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <StatusBadge status={config.enabled ? "sent" : "ignored"} />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-medium ${
                        telegramConnected
                          ? "bg-cyan-100 text-cyan-700"
                          : telegramEnabled
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      Telegram:{" "}
                      {telegramConnected
                        ? "đã kết nối"
                        : telegramEnabled
                        ? "bật nhưng chưa nối group"
                        : "tắt"}
                    </span>
                  </div>

                  <p className="mt-3 text-[11px] text-slate-400">{config.id}</p>
                </div>
              );
            })}
          </div>
        </div>

        {pendingReviews.length > 0 ? (
          <div className="space-y-3">
            <SectionTitle
              title={`Review chờ duyệt (${formatNumber(pendingReviews.length)})`}
              description="Admin có thể can thiệp khi khách đang nóng hoặc người dùng chưa phản hồi kịp."
            />
            <div className="space-y-3">
              {pendingReviews.map((review) => (
                <div
                  key={review.reviewId}
                  className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status="pending" />
                        <LeadBadge state={review.decision?.leadState} />
                        <span className="text-xs text-slate-500">
                          {review.configId}
                        </span>
                      </div>
                      <p className="mt-3 text-xs text-slate-500">Bình luận gốc</p>
                      <p className="mt-1 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">
                        {review.incomingMessage || "—"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedReview((current) =>
                          current === review.reviewId ? "" : review.reviewId
                        )
                      }
                      className="btn-icon"
                    >
                      {expandedReview === review.reviewId ? (
                        <FiChevronUp />
                      ) : (
                        <FiChevronDown />
                      )}
                    </button>
                  </div>

                  {expandedReview === review.reviewId ? (
                    <div className="mt-4 space-y-3">
                      <div>
                        <p className="mb-1 text-xs text-slate-500">AI đề xuất</p>
                        <textarea
                          rows={4}
                          value={
                            editingReply[review.reviewId] ??
                            review.suggestedReply ??
                            ""
                          }
                          onChange={(event) =>
                            setEditingReply((current) => ({
                              ...current,
                              [review.reviewId]: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(review)}
                          disabled={processing === `approve-${review.reviewId}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <FiCheck />
                          Duyệt và gửi
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(review)}
                          disabled={processing === `reject-${review.reviewId}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                        >
                          <FiX />
                          Từ chối
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="space-y-3">
          <SectionTitle
            title="Thread gần nhất"
            description="Ảnh chụp nhanh 30 thread mới cập nhật nhất của user này."
          />
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-3 text-left font-medium">Thread</th>
                  <th className="px-4 py-3 text-left font-medium">Page</th>
                  <th className="px-4 py-3 text-left font-medium">Trạng thái</th>
                  <th className="px-4 py-3 text-left font-medium">Lead</th>
                  <th className="px-4 py-3 text-right font-medium">Cập nhật</th>
                </tr>
              </thead>
              <tbody>
                {(userDetail.threads || []).slice(0, 30).map((thread) => (
                  <tr
                    key={thread.threadKey}
                    className="border-t border-slate-100 hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                        {(thread.threadKey || "").slice(0, 18)}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                        {thread.pageId}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={thread.status} size="xs" />
                    </td>
                    <td className="px-4 py-3">
                      <LeadBadge state={thread.leadState} />
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {formatRelativeTime(thread.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionTitle
        title={`Khách hàng đang dùng (${formatNumber(users.length)})`}
        description="Danh sách account đã tạo config cho Lead Conversion Engine."
      />

      {error ? <ErrorState message={error} onRetry={loadUsers} /> : null}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600">
              <th className="px-4 py-3 text-left font-medium">Khách hàng</th>
              <th className="px-4 py-3 text-center font-medium">Configs</th>
              <th className="px-4 py-3 text-center font-medium">Đang bật</th>
              <th className="px-4 py-3 text-center font-medium">Fanpage</th>
              <th className="px-4 py-3 text-right font-medium">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user.userId}
                className="border-t border-slate-100 hover:bg-slate-50/70"
              >
                <td className="px-4 py-3">
                  <div>
                    <p className="max-w-[220px] truncate font-medium text-slate-900">
                      {user.displayName || "—"}
                    </p>
                    <p className="text-xs text-slate-500">{user.email || "—"}</p>
                    <code className="text-[10px] text-slate-400">
                      {user.userId.slice(0, 14)}...
                    </code>
                  </div>
                </td>
                <td className="px-4 py-3 text-center font-medium text-slate-800">
                  {formatNumber(user.totalConfigs)}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      Number(user.enabledConfigs || 0) > 0
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {formatNumber(user.enabledConfigs)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-slate-600">
                  {formatNumber(user.totalPages)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => loadDetail(user.userId)}
                      disabled={detailLoading}
                      className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    >
                      <FiEye className="h-3 w-3" />
                      Chi tiết
                    </button>
                    {Number(user.enabledConfigs || 0) > 0 ? (
                      <button
                        type="button"
                        onClick={() => handlePauseAll(user.userId)}
                        disabled={processing === user.userId}
                        className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                      >
                        <FiPauseCircle className="h-3 w-3" />
                        Pause
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OperationsTab() {
  const [snapshot, setSnapshot] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("reviews");
  const [statusFilter, setStatusFilter] = useState("");
  const [processing, setProcessing] = useState("");
  const [expandedReview, setExpandedReview] = useState("");
  const [editingReply, setEditingReply] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const tasks = [fetchCommentOperations(60)];

      if (activeSubTab === "queue") {
        tasks.push(fetchCommentQueue({ status: statusFilter || undefined }));
      } else if (activeSubTab === "events") {
        tasks.push(fetchCommentEvents(50));
      }

      const [opsData, extraData] = await Promise.all(tasks);
      setSnapshot(opsData);

      if (activeSubTab === "queue") {
        setQueueItems(extraData || []);
      }

      if (activeSubTab === "events") {
        setEvents(extraData || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeSubTab, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = useCallback(
    async (review) => {
      try {
        setProcessing(`approve-${review.reviewId}`);
        await adminApproveReview({
          userId: review.userId,
          configId: review.configId,
          reviewId: review.reviewId,
          replyText:
            editingReply[review.reviewId] || review.suggestedReply || "",
        });
        notifyReloadRequired(
          "Đã duyệt review. Reload trang để xem snapshot Lead Engine mới nhất."
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setProcessing("");
      }
    },
    [editingReply]
  );

  const handleReject = useCallback(
    async (review) => {
      try {
        setProcessing(`reject-${review.reviewId}`);
        await adminRejectReview({
          userId: review.userId,
          configId: review.configId,
          reviewId: review.reviewId,
          reason: "Rejected by admin",
        });
        notifyReloadRequired(
          "Đã từ chối review. Reload trang để xem snapshot Lead Engine mới nhất."
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setProcessing("");
      }
    },
    []
  );

  const handleFlush = useCallback(async () => {
    if (!window.confirm("Bạn chắc chắn muốn requeue tất cả items bị stuck?")) {
      return;
    }

    try {
      setProcessing("flush");
      await flushStaleQueueItems();
      notifyReloadRequired(
        "Đã flush queue stale. Reload trang để xem snapshot Lead Engine mới nhất."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing("");
    }
  }, []);

  const handlePurge = useCallback(async () => {
    const firstConfirm = window.confirm(
      "Cảnh báo: thao tác này sẽ xóa toàn bộ queue. Tiếp tục?"
    );
    if (!firstConfirm) {
      return;
    }

    const secondConfirm = window.confirm(
      "Xác nhận lần hai: tất cả item trong queue sẽ mất vĩnh viễn."
    );
    if (!secondConfirm) {
      return;
    }

    try {
      setProcessing("purge");
      await purgeCommentQueue();
      notifyReloadRequired(
        "Đã purge queue. Reload trang để xem snapshot Lead Engine mới nhất."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing("");
    }
  }, []);

  if (loading && !snapshot) {
    return <LoadingState label="Đang tải trung tâm vận hành..." />;
  }

  if (error && !snapshot) {
    return <ErrorState message={error} onRetry={load} />;
  }

  const pendingReviews = snapshot?.pendingReviews || [];
  const attentionThreads = snapshot?.attentionThreads || [];
  const queue = snapshot?.queue || {};
  const summary = snapshot?.summary || {};

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Trung tâm vận hành"
        description="Ưu tiên case cần duyệt, lead cần follow-up và sức khỏe queue trong cùng một màn."
        actions={
          <>
            <button
              type="button"
              onClick={handleFlush}
              disabled={processing === "flush"}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
            >
              <FiZap />
              Flush stale
            </button>
            <button
              type="button"
              onClick={handlePurge}
              disabled={processing === "purge"}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              <FiTrash2 />
              Purge queue
            </button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          icon={FiClock}
          label="Review chờ duyệt"
          value={summary.pendingReviews || 0}
          color="amber"
        />
        <StatCard
          icon={FiEye}
          label="Case cần follow-up"
          value={summary.attentionThreads || 0}
          color="violet"
        />
        <StatCard
          icon={FiAlertTriangle}
          label="Hot lead ưu tiên"
          value={summary.hotAttentionThreads || 0}
          color="rose"
        />
        <StatCard
          icon={FiDatabase}
          label="Queue stale"
          value={summary.staleQueue || 0}
          sub={`${formatNumber(queue.queued || 0)} queued • ${formatNumber(
            queue.processing || 0
          )} processing`}
          color={Number(summary.staleQueue || 0) > 0 ? "rose" : "slate"}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {[
          { key: "reviews", label: "Review", icon: FiCheckCircle },
          { key: "followups", label: "Follow-up", icon: FiEye },
          { key: "queue", label: "Queue", icon: FiDatabase },
          { key: "events", label: "Events", icon: FiActivity },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveSubTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                activeSubTab === tab.key
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {error ? <ErrorState message={error} onRetry={load} /> : null}

      {activeSubTab === "reviews" ? (
        pendingReviews.length === 0 ? (
          <EmptyState
            icon={FiCheckCircle}
            title="Không có review nào đang chờ"
            description="Hiện tại chưa có case nào cần admin duyệt thay người dùng."
          />
        ) : (
          <div className="space-y-3">
            {pendingReviews.map((review) => (
              <div
                key={review.reviewId}
                className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status="pending" />
                      <LeadBadge state={review.leadState} />
                      <span className="text-xs text-slate-500">
                        {review.userDisplayName || review.userId}
                      </span>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-500">
                        {review.pageName}
                      </span>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-500">
                        {review.configName}
                      </span>
                    </div>

                    <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_1fr]">
                      <div>
                        <p className="text-xs text-slate-500">Khách hàng</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {review.customerName || "Khách hàng"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Intent</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {review.intent || "—"}
                        </p>
                      </div>
                    </div>

                    <p className="mt-4 text-xs text-slate-500">Bình luận gốc</p>
                    <p className="mt-1 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">
                      {review.incomingMessage || "—"}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {review.commentLink ? (
                      <a
                        href={review.commentLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <FiExternalLink />
                        Mở Facebook
                      </a>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedReview((current) =>
                          current === review.reviewId ? "" : review.reviewId
                        )
                      }
                      className="btn-icon"
                    >
                      {expandedReview === review.reviewId ? (
                        <FiChevronUp />
                      ) : (
                        <FiChevronDown />
                      )}
                    </button>
                  </div>
                </div>

                {expandedReview === review.reviewId ? (
                  <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
                    <div>
                      <p className="mb-1 text-xs text-slate-500">
                        Nội dung phản hồi
                      </p>
                      <textarea
                        rows={4}
                        value={
                          editingReply[review.reviewId] ??
                          review.suggestedReply ??
                          ""
                        }
                        onChange={(event) =>
                          setEditingReply((current) => ({
                            ...current,
                            [review.reviewId]: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs text-slate-500">
                        Tạo lúc {formatDate(review.createdAt)} • cập nhật{" "}
                        {formatRelativeTime(review.updatedAt)}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(review)}
                          disabled={processing === `approve-${review.reviewId}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <FiCheck />
                          Duyệt và gửi
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(review)}
                          disabled={processing === `reject-${review.reviewId}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                        >
                          <FiX />
                          Từ chối
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )
      ) : null}

      {activeSubTab === "followups" ? (
        attentionThreads.length === 0 ? (
          <EmptyState
            icon={FiEye}
            title="Không có case follow-up mở"
            description="Hiện không có thread manual follow-up hoặc needs review ở mức toàn hệ thống."
          />
        ) : (
          <div className="space-y-3">
            {attentionThreads.map((thread) => (
              <div
                key={`${thread.userId}-${thread.pageId}-${thread.threadKey}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <LeadBadge state={thread.leadState} />
                      <StatusBadge status={thread.status} />
                      <span className="text-xs text-slate-500">
                        {thread.userDisplayName || thread.userId}
                      </span>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-500">
                        {thread.pageName}
                      </span>
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <p className="text-xs text-slate-500">Khách hàng</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {thread.customerName || "Khách hàng"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Intent</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {thread.intent || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Config</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {thread.configName || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Cập nhật</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {formatRelativeTime(thread.lastInboundAt || thread.updatedAt)}
                        </p>
                      </div>
                    </div>

                    <p className="mt-4 text-xs text-slate-500">Tin nhắn gần nhất</p>
                    <p className="mt-1 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">
                      {thread.latestMessage || "—"}
                    </p>

                    {thread.reason ? (
                      <>
                        <p className="mt-4 text-xs text-slate-500">
                          Lý do chuyển sang thủ công
                        </p>
                        <p className="mt-1 text-sm text-slate-700">
                          {thread.reason}
                        </p>
                      </>
                    ) : null}
                  </div>

                  {thread.commentLink ? (
                    <a
                      href={thread.commentLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <FiExternalLink />
                      Mở Facebook
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {activeSubTab === "queue" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {["", "queued", "processing"].map((status) => (
              <button
                key={status || "all"}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  statusFilter === status
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {status === ""
                  ? "Tất cả"
                  : status === "queued"
                  ? "Đợi xử lý"
                  : "Đang xử lý"}
              </button>
            ))}
          </div>

          {loading && !queueItems.length ? (
            <LoadingState label="Đang tải queue..." />
          ) : queueItems.length === 0 ? (
            <EmptyState
              icon={FiDatabase}
              title="Queue đang trống"
              description="Hiện chưa có item nào trong hàng đợi hoặc bộ lọc không có dữ liệu."
            />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="px-4 py-3 text-left font-medium">Queue ID</th>
                    <th className="px-4 py-3 text-left font-medium">User</th>
                    <th className="px-4 py-3 text-left font-medium">Page</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">Tạo lúc</th>
                    <th className="px-4 py-3 text-right font-medium">Claimed</th>
                  </tr>
                </thead>
                <tbody>
                  {queueItems.map((item) => (
                    <tr
                      key={item.queueId}
                      className="border-t border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="px-4 py-3">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                          {(item.queueId || "").slice(0, 14)}...
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                          {(item.userId || "").slice(0, 12)}...
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                          {item.pageId}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={item.status} size="xs" />
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-slate-500">
                        {formatRelativeTime(item.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-slate-500">
                        {item.claimedAt ? formatRelativeTime(item.claimedAt) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {activeSubTab === "events" ? (
        loading && !events.length ? (
          <LoadingState label="Đang tải events..." />
        ) : events.length === 0 ? (
          <EmptyState
            icon={FiActivity}
            title="Chưa có event gần đây"
            description="Webhook event mới sẽ xuất hiện ở đây để admin đối soát với queue."
          />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-3 text-left font-medium">Event</th>
                  <th className="px-4 py-3 text-left font-medium">Page</th>
                  <th className="px-4 py-3 text-left font-medium">Verb</th>
                  <th className="px-4 py-3 text-left font-medium">Message</th>
                  <th className="px-4 py-3 text-right font-medium">Thời gian</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr
                    key={event.eventId}
                    className="border-t border-slate-100 hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                        {(event.eventId || "").slice(0, 14)}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">
                        {event.pageId}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        {event.verb || "—"}
                      </span>
                    </td>
                    <td className="max-w-[280px] truncate px-4 py-3 text-slate-600">
                      {event.message || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {formatRelativeTime(event.persistedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}

function MetricsTab() {
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [userMetrics, setUserMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("7d");
  const [activeSubTab, setActiveSubTab] = useState("daily");
  const [error, setError] = useState("");

  const getDateParams = useCallback(() => {
    const to = new Date().toISOString().slice(0, 10);
    const days = dateRange === "30d" ? 30 : dateRange === "14d" ? 14 : 7;
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    return { from, to };
  }, [dateRange]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = getDateParams();

      if (activeSubTab === "daily") {
        const data = await fetchCommentMetrics(params);
        setDailyMetrics(data);
      } else {
        const data = await fetchCommentMetricsPerUser(params);
        setUserMetrics(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeSubTab, getDateParams]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Analytics"
        description="Xem volume xử lý theo thời gian và so sánh mức dùng giữa các khách hàng."
        actions={
          <>
            {["7d", "14d", "30d"].map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setDateRange(range)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  dateRange === range
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {range === "7d" ? "7 ngày" : range === "14d" ? "14 ngày" : "30 ngày"}
              </button>
            ))}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {[
          { key: "daily", label: "Theo ngày", icon: FiBarChart2 },
          { key: "users", label: "Theo khách hàng", icon: FiUsers },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveSubTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                activeSubTab === tab.key
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {error ? <ErrorState message={error} onRetry={load} /> : null}

      {loading ? (
        <LoadingState label="Đang tải analytics..." />
      ) : activeSubTab === "daily" ? (
        dailyMetrics.length === 0 ? (
          <EmptyState
            icon={FiBarChart2}
            title="Chưa có dữ liệu trong khoảng này"
            description="Thử đổi mốc thời gian hoặc chờ hệ thống phát sinh thêm metrics."
          />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-2 gap-3 border-b border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-4">
              <StatCard
                icon={FiMessageCircle}
                label="Tổng nhận"
                value={dailyMetrics.reduce(
                  (sum, item) => sum + Number(item.received || 0),
                  0
                )}
                color="blue"
              />
              <StatCard
                icon={FiSend}
                label="Tổng gửi"
                value={dailyMetrics.reduce(
                  (sum, item) => sum + Number(item.sent || 0),
                  0
                )}
                color="emerald"
              />
              <StatCard
                icon={FiAlertTriangle}
                label="Tổng lỗi"
                value={dailyMetrics.reduce(
                  (sum, item) => sum + Number(item.failed || 0),
                  0
                )}
                color="rose"
              />
              <StatCard
                icon={FiZap}
                label="Tổng AI Calls"
                value={dailyMetrics.reduce(
                  (sum, item) => sum + Number(item.aiCalls || 0),
                  0
                )}
                color="amber"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="px-4 py-3 text-left font-medium">Ngày</th>
                    <th className="px-4 py-3 text-right font-medium">Nhận</th>
                    <th className="px-4 py-3 text-right font-medium">Gửi</th>
                    <th className="px-4 py-3 text-right font-medium">Bỏ qua</th>
                    <th className="px-4 py-3 text-right font-medium">Lỗi</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Cần follow-up
                    </th>
                    <th className="px-4 py-3 text-right font-medium">AI Calls</th>
                    <th className="px-4 py-3 text-right font-medium">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyMetrics.map((day) => (
                    <tr
                      key={day.date}
                      className="border-t border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {day.date}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatNumber(day.received)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">
                        {formatNumber(day.sent)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-500">
                        {formatNumber(day.ignored)}
                      </td>
                      <td className="px-4 py-3 text-right text-rose-600">
                        {formatNumber(day.failed)}
                      </td>
                      <td className="px-4 py-3 text-right text-violet-600">
                        {formatNumber(day.manual_followup || day.needs_review)}
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600">
                        {formatNumber(day.aiCalls)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-500">
                        {(Number(day.tokensApprox || 0) / 1000).toFixed(1)}K
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : userMetrics.length === 0 ? (
        <EmptyState
          icon={FiUsers}
          title="Chưa có dữ liệu theo khách hàng"
          description="Khi người dùng bắt đầu phát sinh metrics, bảng so sánh sẽ xuất hiện tại đây."
        />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-4 py-3 text-left font-medium">Khách hàng</th>
                <th className="px-4 py-3 text-right font-medium">Nhận</th>
                <th className="px-4 py-3 text-right font-medium">Gửi</th>
                <th className="px-4 py-3 text-right font-medium">Bỏ qua</th>
                <th className="px-4 py-3 text-right font-medium">Lỗi</th>
                <th className="px-4 py-3 text-right font-medium">AI Calls</th>
                <th className="px-4 py-3 text-right font-medium">Hit rate</th>
              </tr>
            </thead>
            <tbody>
              {userMetrics.map((user) => {
                const hitRate =
                  Number(user.received || 0) > 0
                    ? (
                        (Number(user.sent || 0) / Number(user.received || 0)) *
                        100
                      ).toFixed(0)
                    : "0";

                return (
                  <tr
                    key={user.userId}
                    className="border-t border-slate-100 hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="max-w-[180px] truncate font-medium text-slate-900">
                          {user.displayName || "—"}
                        </p>
                        <p className="text-[11px] text-slate-400">{user.email || "—"}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatNumber(user.received)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {formatNumber(user.sent)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500">
                      {formatNumber(user.ignored)}
                    </td>
                    <td className="px-4 py-3 text-right text-rose-600">
                      {formatNumber(user.failed)}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-600">
                      {formatNumber(user.aiCalls)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          Number(hitRate) >= 70
                            ? "bg-emerald-100 text-emerald-700"
                            : Number(hitRate) >= 40
                            ? "bg-amber-100 text-amber-700"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {hitRate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const AdminCommentManagement = ({
  initialTab = "overview",
  routeMode = false,
}) => {
  const location = useLocation();
  const resolvedTab = useMemo(
    () =>
      routeMode
        ? resolveActiveTab(location.pathname, initialTab)
        : initialTab || "overview",
    [initialTab, location.pathname, routeMode]
  );
  const [activeTab, setActiveTab] = useState(resolvedTab);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-sm font-medium text-cyan-700">
              <FiShield />
              Lead Conversion Engine
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
              Quản trị Lead Conversion Engine
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Màn hình admin để theo dõi khách hàng đang dùng, xử lý review,
              kiểm soát queue và đọc analytics vận hành của engine xử lý lead từ
              comment.
            </p>
          </div>

          {routeMode ? (
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <FiArrowLeft />
              Về dashboard admin
            </Link>
          ) : null}
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <nav className="flex flex-wrap items-center gap-2">
            {TAB_CONFIG.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              const className = `inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                isActive
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`;

              if (routeMode) {
                return (
                  <Link key={tab.key} to={tab.href} className={className}>
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </Link>
                );
              }

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={className}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Dữ liệu Lead Engine admin là snapshot một lần theo view. Để thấy dữ liệu
        mới nhất sau khi có thay đổi, hãy reload trang.
      </div>

      {activeTab === "overview" ? <OverviewTab /> : null}
      {activeTab === "users" ? <UsersTab /> : null}
      {activeTab === "operations" ? <OperationsTab /> : null}
      {activeTab === "metrics" ? <MetricsTab /> : null}

      <style>{`
        .btn-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          color: #64748b;
          background: white;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-icon:hover {
          background: #f8fafc;
          color: #0f172a;
        }
      `}</style>
    </div>
  );
};

export default AdminCommentManagement;
